import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldError, FormSummary, RequiredLegend, Req, focusFirstError, clean, POSTAL_ERROR, PHONE_ERROR, EMAIL_ERROR } from "./Field";
import { CallbackDialog } from "./CallbackDialog";
import { PromoField, promoDiscount } from "./PromoField";
import { useProduct, eur, eur2 } from "@/lib/product";
import { API, apiError } from "@/lib/api";
import { track, PRODUCT_ID, checkoutMeta } from "@/lib/metaPixel";

const INITIAL = { quantity: 1, first_name: "", last_name: "", email: "", phone: "", street: "", postal_code: "", city: "" };

const validate = (f, accept) => clean({
  first_name: f.first_name.trim().length < 2 && "Le prénom est obligatoire.",
  last_name: f.last_name.trim().length < 2 && "Le nom est obligatoire.",
  email: EMAIL_ERROR(f.email),
  phone: PHONE_ERROR(f.phone),
  street: f.street.trim().length < 2 && "L'adresse est obligatoire.",
  postal_code: POSTAL_ERROR(f.postal_code.trim()),
  city: f.city.trim().length < 2 && "La ville est obligatoire.",
  accept_cgv: !accept && "Vous devez accepter les CGV pour commander.",
});

export const OrderForm = () => {
  const { product } = useProduct();
  const [f, setF] = useState(INITIAL);
  const [accept, setAccept] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState(null);

  const set = (k) => (ev) => {
    setF({ ...f, [k]: ev.target.value });
    if (errors[k]) setErrors(({ [k]: _, ...rest }) => rest);
  };
  const input = (k, props = {}) => (
    <input id={`order-${k}`} data-testid={`order-${k}-input`} value={f[k]} onChange={set(k)} className="field-input" aria-invalid={!!errors[k]} aria-required="true" {...props} />
  );

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    const e = validate(f, accept);
    setErrors(e);
    if (Object.keys(e).length) return focusFirstError(ev.currentTarget);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders/checkout`, { ...f, quantity: Number(f.quantity), accept_cgv: accept, origin_url: window.location.origin, promo_code: promo?.code || null, ...checkoutMeta() });
      const tracked = track("InitiateCheckout", { value: total, currency: "EUR", num_items: Number(f.quantity), content_ids: [PRODUCT_ID], content_type: "product" }, `IC-${data.order_id}`);
      setTimeout(() => { window.location.href = data.checkout_url; }, tracked ? 400 : 0);
    } catch (err) {
      setServerError(apiError(err));
      setLoading(false);
    }
  };

  const subtotal = product ? product.current_price * Number(f.quantity) : 0;
  const discount = promoDiscount(promo, subtotal);
  const total = product ? subtotal - discount + product.shipping_fee : null;

  return (
    <form onSubmit={submit} noValidate data-testid="order-form" className="flex flex-col gap-5">
      <div>
        <h3 className="font-display text-2xl font-bold text-slate-900">Vos informations de livraison</h3>
        <p className="text-sm text-slate-500 mt-1">Livraison en France métropolitaine continentale uniquement.</p>
        <div className="mt-2"><RequiredLegend testId="order-required-legend" /></div>
      </div>
      <Field id="order-quantity" label="Quantité">
        <select id="order-quantity" data-testid="order-quantity-select" value={f.quantity} onChange={set("quantity")} className="field-input sm:max-w-[10rem]">
          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="order-first_name" label="Prénom" required error={errors.first_name}>{input("first_name", { autoComplete: "given-name" })}</Field>
        <Field id="order-last_name" label="Nom" required error={errors.last_name}>{input("last_name", { autoComplete: "family-name" })}</Field>
        <Field id="order-email" label="E-mail" required error={errors.email}>{input("email", { type: "email", autoComplete: "email" })}</Field>
        <Field id="order-phone" label="Téléphone" required error={errors.phone}>{input("phone", { type: "tel", autoComplete: "tel", placeholder: "06 12 34 56 78" })}</Field>
      </div>
      <Field id="order-street" label="Adresse (n° et rue)" required error={errors.street}>{input("street", { autoComplete: "street-address" })}</Field>
      <div className="grid grid-cols-[0.8fr_1.2fr] gap-4">
        <Field id="order-postal_code" label="Code postal" required error={errors.postal_code}>{input("postal_code", { inputMode: "numeric", maxLength: 5, autoComplete: "postal-code" })}</Field>
        <Field id="order-city" label="Ville" required error={errors.city}>{input("city", { autoComplete: "address-level2" })}</Field>
      </div>
      <PromoField quantity={f.quantity} promo={promo} onChange={setPromo} />
      {product && (
        <div data-testid="order-summary" className="rounded-2xl bg-slate-50 border border-slate-200 px-5 divide-y divide-slate-200 text-sm">
          <div className="flex justify-between py-3 text-slate-600"><span>Midea PortaSplit × {f.quantity}</span><span data-testid="order-unit-price">{eur(product.current_price * Number(f.quantity))}</span></div>
          {discount > 0 && <div className="flex justify-between py-3 text-emerald-700"><span>Code {promo.code}</span><span data-testid="order-discount">−{eur2(discount)}</span></div>}
          <div className="flex justify-between py-3 text-slate-600"><span>Livraison (par commande)</span><span data-testid="order-shipping-fee">{eur(product.shipping_fee)}</span></div>
          <div className="flex justify-between py-3 font-display text-lg font-bold text-slate-900"><span>Total TTC</span><span data-testid="order-total-price">{discount > 0 ? eur2(total) : eur(total)}</span></div>
          {product.klarna_enabled && <div data-testid="order-klarna-summary" className="py-3 text-slate-600">
            ou en 3x ou 4x avec <strong className="text-slate-900">Klarna</strong> : 3 × {eur2(Math.round((total / 3) * 100) / 100)} ou 4 × {eur2(Math.round((total / 4) * 100) / 100)}
            <span className="block text-xs text-slate-400">Choix du mode de paiement sur la page Stripe. Sous réserve d'acceptation par Klarna, selon les conditions Klarna.</span>
          </div>}
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
          <Checkbox data-testid="order-accept-cgv-checkbox" checked={accept} aria-invalid={!!errors.accept_cgv}
            onCheckedChange={(v) => { setAccept(v === true); if (v === true) setErrors(({ accept_cgv: _, ...rest }) => rest); }}
            className={`mt-0.5 ${errors.accept_cgv ? "border-red-600 ring-2 ring-red-200" : ""}`} />
          <span>J'ai lu et j'accepte les <Link to="/cgv" target="_blank" data-testid="order-cgv-link" className="text-sky-700 font-semibold underline underline-offset-4">conditions générales de vente</Link>, notamment le droit de rétractation de 14 jours. <Req /></span>
        </label>
        <div className="pl-7"><FieldError id="order-accept_cgv" error={errors.accept_cgv} /></div>
      </div>
      <FormSummary errors={errors} testId="order-form-summary" />
      {serverError && <div data-testid="order-server-error" className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{serverError}</div>}
      <div className="flex flex-col sm:flex-row gap-3">
        <button type="submit" disabled={loading} data-testid="order-cta-button" className="btn-heat text-base disabled:opacity-70">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Redirection…</> : <>Payer et commander <ArrowRight className="w-5 h-5" /></>}
        </button>
        <CallbackDialog />
      </div>
      <p className="text-xs text-slate-500">Vous serez redirigé vers la page de paiement sécurisée Stripe. Le montant est calculé par nos serveurs. Nous pouvons vous envoyer un rappel par e-mail si votre commande n'est pas finalisée (désinscription possible dans chaque rappel).</p>
    </form>
  );
};
